import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../axiosConfig';

// Track a visit
export const trackVisit = createAsyncThunk('visits/track', async (visitData) => {
  try {
    console.log('Sending visit data to backend:', visitData);
    const res = await axios.post('/visits/track', visitData, { withCredentials: true });
    console.log('Visit tracking response:', res.data);
    return res.data;
  } catch (error) {
    console.error('Visit tracking error:', error.response?.data || error.message);
    throw error;
  }
});

// Get visit analytics
export const fetchVisitAnalytics = createAsyncThunk('visits/fetchAnalytics', async ({ groupBy = 'day', tier = 'free', startDate, endDate }) => {
  try {
    console.log('Fetching visit analytics with params:', { groupBy, tier, startDate, endDate });
    const params = new URLSearchParams();
    params.append('groupBy', groupBy);
    params.append('tier', tier);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const res = await axios.get(`/visits/analytics?${params}`, { withCredentials: true });
    console.log('Visit analytics response:', res.data);
    return res.data;
  } catch (error) {
    console.error('Visit analytics error:', error.response?.data || error.message);
    throw error;
  }
});

// Update visit duration
export const updateVisitDuration = createAsyncThunk('visits/updateDuration', async ({ sessionId, duration }) => {
  const res = await axios.patch(`/visits/duration/${sessionId}`, { duration }, { withCredentials: true });
  return res.data;
});

// Mark conversion
export const markConversion = createAsyncThunk('visits/markConversion', async ({ sessionId, conversionType, conversionValue = 0 }) => {
  try {
    console.log('Sending conversion data to backend:', { sessionId, conversionType, conversionValue });
    const res = await axios.patch(`/visits/convert/${sessionId}`, { conversionType, conversionValue }, { withCredentials: true });
    console.log('Conversion tracking response:', res.data);
    return res.data;
  } catch (error) {
    console.error('Conversion tracking error:', error.response?.data || error.message);
    throw error;
  }
});

// Get QR code analytics
export const fetchQRAnalytics = createAsyncThunk('visits/fetchQRAnalytics', async ({ startDate, endDate, qrType } = {}) => {
  try {
    let url = '/visits/qr-analytics?';
    if (startDate) url += `startDate=${startDate}&`;
    if (endDate) url += `endDate=${endDate}&`;
    if (qrType) url += `qrType=${qrType}`;
    console.log('Fetching QR analytics with params:', { startDate, endDate, qrType });
    const res = await axios.get(url, { withCredentials: true });
    console.log('QR analytics response:', res.data);
    return res.data;
  } catch (error) {
    console.error('QR analytics error:', error.response?.data || error.message);
    throw error;
  }
});

const visitSlice = createSlice({
  name: 'visits',
  initialState: {
    analytics: null,
    qrAnalytics: null,
    status: 'idle',
    qrStatus: 'idle',
    error: null,
    currentVisit: null,
    isTracking: false
  },
  reducers: {
    setCurrentVisit: (state, action) => {
      state.currentVisit = action.payload;
    },
    setTracking: (state, action) => {
      state.isTracking = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearAnalytics: (state) => {
      state.analytics = null;
      state.status = 'idle';
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Track visit
      .addCase(trackVisit.pending, (state) => {
        state.isTracking = true;
      })
      .addCase(trackVisit.fulfilled, (state, action) => {
        state.isTracking = false;
        state.currentVisit = action.payload.visit;
      })
      .addCase(trackVisit.rejected, (state, action) => {
        state.isTracking = false;
        state.error = action.error.message;
      })
      // Fetch analytics
      .addCase(fetchVisitAnalytics.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchVisitAnalytics.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.analytics = action.payload.analytics;
      })
      .addCase(fetchVisitAnalytics.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Update duration
      .addCase(updateVisitDuration.fulfilled, (state, action) => {
        if (state.currentVisit && state.currentVisit.sessionId === action.payload.visit.sessionId) {
          state.currentVisit = action.payload.visit;
        }
      })
      // Mark conversion
      .addCase(markConversion.fulfilled, (state, action) => {
        if (state.currentVisit && state.currentVisit.sessionId === action.payload.visit.sessionId) {
          state.currentVisit = action.payload.visit;
        }
      })
      // Fetch QR analytics
      .addCase(fetchQRAnalytics.pending, (state) => {
        state.qrStatus = 'loading';
      })
      .addCase(fetchQRAnalytics.fulfilled, (state, action) => {
        state.qrStatus = 'succeeded';
        state.qrAnalytics = action.payload.analytics;
      })
      .addCase(fetchQRAnalytics.rejected, (state, action) => {
        state.qrStatus = 'failed';
        state.error = action.error.message;
      });
  }
});

export const { setCurrentVisit, setTracking, clearError, clearAnalytics } = visitSlice.actions;
export default visitSlice.reducer;
